<template lang="pug">
div
  UiCardSection
    .ui-card-section-title {{ $t("companies.tabs.sales_invoices") }}
  UiCardSection
    RestTable(
      namespace="sales_invoices"
      :headers="headers"
      ref="salesInvoicesTable"
      :fetch-handler="getData"
      :initial-filters="initialFilters"
      embed
    )
      template(#filters="{ filters, updateFilters }")
        UiStack(align="end")
          UiStackItem(fill)
            UiFormGroup(:label="$t('companies.sales_invoices.status_filter')")
              UiMultiselect(
                :modelValue="filters.status"
                :options="statesOptions"
                label="text"
                @update:modelValue="updateFilters({ status: $event })"
              )
          UiStackItem(fill)
            UiFormGroup(:label="$t('companies.sales_invoices.search')")
              UiInput(
                :modelValue="filters.search"
                type="search"
                @update:modelValue="updateFilters({ search: $event })"
              )
          UiStackItem
            UiButton(
              variant="primary"
              @click="restartAll"
              icon="RefreshCw"
            ) {{ $t("companies.sales_invoices.actions.restart") }}
          UiStackItem
            UiButton(
              variant="danger"
              @click="deleteFailed"
              icon="Trash"
            ) {{ $t("companies.sales_invoices.actions.delete") }}
          UiStackItem
            UiButton(
              variant="warning"
              @click="deleteProcessed"
              icon="Trash"
            ) {{ $t("companies.sales_invoices.actions.delete_processed") }}
          UiStackItem
            UiButton(
              variant="primary"
              @click="exportFailed"
              icon="FileDownload"
            ) {{ $t("companies.sales_invoices.actions.export_failed") }}
      template(#cell:invoice_number="{ item }")
        a(@click="showLines(item)") {{ item.invoice_number }}
      template(#cell:status="{ item }")
        UiButton.uppercase(
          :variant="STATUSES[item.status] || 'info'"
          :title="`Status reason: \"${item.status_reason}\"`"
        ) {{ $t(`companies.sales_invoices.statuses.${item.status}`) }}
      template(#cell:actions="{ item }")
        UiStack(spacing="xs")
          UiStackItem
            UiButton(
              size="sm"
              variant="smooth-danger"
              @click="deleteInvoice(item)"
              icon="Trash"
            ) {{ $t("actions.delete") }}
  ShowSalesInvoiceModal(ref="ShowSalesInvoiceModal")
  MaventaInvoicesTab
  ScopeVouchersTab
</template>

<script>
import ApiScopeSalesInvoices from "@/api/scopeSalesInvoices.js";
import ShowSalesInvoiceModal from "@/modals/Companies/showSalesInvoice.vue";
import MaventaInvoicesTab from "@/views/Companies/Tabs/maventa_invoices.vue";
import ScopeVouchersTab from "@/views/Companies/Tabs/scope_vouchers.vue";

const STATUSES = {
  draft: "info",
  processing: "warning",
  customer_created: "warning",
  processed: "success",
  failed: "danger",
};

export default {
  components: {
    ShowSalesInvoiceModal,
    MaventaInvoicesTab,
    ScopeVouchersTab,
  },
  data() {
    return {
      inProgress: false,
      initialFilters: {
        status: null,
        search: "",
      },
      pagination: {
        sortBy: null,
        sortOrder: null,
      },
      states: [],
      STATUSES: STATUSES,
      headers: [
        {
          key: "id",
          label: "#",
          squeeze: true,
        },
        {
          key: "job_id",
          label: "Job id",
        },
        {
          key: "created_at",
          label: "Created at",
          squeeze: false,
        },
        {
          key: "invoice_number",
          label: "Invoice number",
          squeeze: false,
        },
        {
          key: "status",
          label: "Status",
          squeeze: false,
          sortable: true,
        },
        {
          key: "status_reason",
          label: "Status Reason",
          squeeze: false,
        },
        {
          key: "actions",
          label: "",
          squeeze: true,
        },
      ],
    };
  },

  computed: {
    statesOptions() {
      let first_element = { text: "All Invoices", value: null };
      let options = this.states.map((i) => {
        return { text: i[0], value: i[1] };
      });
      options.unshift(first_element);
      return options;
    },
  },

  methods: {
    reload() {
      this.$refs.salesInvoicesTable.fetchData();
    },

    getData(params, config) {
      return ApiScopeSalesInvoices.fetchInvoices(
        this.$route.params.id,
        params,
        config,
      ).then((resp) => {
        return {
          items: resp.data.scope_sales_invoices,
          meta: resp.data.meta,
        };
      });
    },
    fetchStates() {
      ApiScopeSalesInvoices.fetchStates(this.$route.params.id).then((resp) => {
        this.states = resp.data.states;
      });
    },

    restartAll() {
      ApiScopeSalesInvoices.restartInvoices(this.$route.params.id).then(
        (resp) => {
          this.reload();
        },
      );
    },

    deleteFailed() {
      this.$dialogConfirm({
        title: this.$t("actions.remove"),
        description: this.$t("companies.sales_invoices.actions.confirm_delete"),
        variant: "danger",
        icon: "lan-disconnect",
        okLabel: this.$t("actions.remove"),
        okVariant: "danger",
        okIcon: "Trash",
        cancelVariant: "smooth-secondary",
      }).then((result) => {
        if (!result) return;
        ApiScopeSalesInvoices.deleteFailedInvoices(this.$route.params.id).then(
          (resp) => {
            this.reload();
          },
        );
      });
    },

    deleteProcessed() {
      this.$dialogConfirm({
        title: this.$t("actions.remove"),
        description: this.$t("companies.sales_invoices.actions.confirm_delete"),
        variant: "danger",
        icon: "lan-disconnect",
        okLabel: this.$t("actions.remove"),
        okVariant: "danger",
        okIcon: "Trash",
        cancelVariant: "smooth-secondary",
      }).then((result) => {
        if (!result) return;
        ApiScopeSalesInvoices.deleteProcessedInvoices(
          this.$route.params.id,
        ).then((resp) => {
          this.reload();
        });
      });
    },

    exportFailed() {
      ApiScopeSalesInvoices.exportFailed(this.$route.params.id)
        .then((resp) => {
          let fileURL = window.URL.createObjectURL(new Blob([resp.data]));
          let fileLink = document.createElement("a");
          fileLink.href = fileURL;
          fileLink.setAttribute("download", `failed_invoices.xlsx`);
          document.body.appendChild(fileLink);
          fileLink.click();
        })
        .catch((e) => {});
    },

    showLines(invoice) {
      this.$refs.ShowSalesInvoiceModal.open(invoice.id);
    },

    deleteInvoice(item) {
      this.$dialogConfirm({
        title: this.$t("actions.remove"),
        description: this.$t("actions.confirm_remove"),
        variant: "danger",
        icon: "lan-disconnect",
        okLabel: this.$t("actions.remove"),
        okVariant: "danger",
        okIcon: "Trash",
        cancelVariant: "smooth-secondary",
      }).then((result) => {
        if (!result) return;
        ApiScopeSalesInvoices.deleteInvoice(
          this.$route.params.id,
          item.id,
        ).then((resp) => {
          this.reload();
        });
      });
    },
  },

  created() {
    this.fetchStates();
  },
};
</script>
